import React, { useEffect, Suspense, useState, useMemo, useRef } from 'react';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import LayoutActions from '../redux/actions/LayoutActions';
import { Box } from '@mui/material';
import TopNavigation from '../components/TopNavigation/TopNavigation';
import Loading from '../components/loaders/Loading';
import Header from '../components/Header';
import { GetScreenSize } from '../utils/screenSize';
import { getPagePermissionByPath } from '../utils/utils';
import UserRoute from '../routers/UserRoute';
import SetupLayout from './SetupLayout';
import SettingsLayout from './SettingsLayout';
import DeductionRoutingLayout from './DeductionRoutingLayout';
import StockPrintLayout from './StockPrintLayout';
import { matchPath } from 'react-router-dom/cjs/react-router-dom.min';
import styled from '@emotion/styled';
import { layoutPageComponents as components } from './components/layoutPageComponents';
import { isEmbedded } from '../utils/embed';
import { withTempPrintSettingsPages } from './components/tempPrintSettingsPages';
import { subscribeToRouteSync } from '../utils/routeSyncBridge';

const getBasename = () => window.__ADMIN_STOCK_BASENAME__ || '';

/** Strip host shell prefix (e.g. /admin) without matching /administrator */
const stripBasename = (path) => {
  const base = getBasename();
  if (!base) return path;
  if (path === base || path === `${base}/`) return '/';
  if (path.startsWith(`${base}/`)) return path.slice(base.length) || '/';
  return path;
};

const normalizePathForCompare = (p) => {
  let s = p || '/';
  try {
    s = decodeURIComponent(s);
  } catch (e) {
    /* ignore */
  }
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s || '/';
};

const pathsMatch = (a, b) => normalizePathForCompare(a) === normalizePathForCompare(b);

const getLayoutSegment = (pathname) => {
  return stripBasename(pathname).split('/')[2] || '';
};


const reformat = (data) => {
  let final = [];
  data?.map((permission) => {
    if (!permission.isPopup || (permission.isPopup && permission.route)) {
      if (permission.pageType === "layout") {
        // TEMP: add print-settings pages whose CMS rows aren't seeded yet (see tempPrintSettingsPages.js)
        let obj = withTempPrintSettingsPages({ ...permission });
        obj.subLayouts = [];
        let tmp = permission.subPage?.filter(
          (perm) => perm.pageType === "layout"
        ).map(withTempPrintSettingsPages);
        if (tmp?.length > 0) obj.subLayouts = tmp;
        final.push(obj);
      } else {
        final.push(permission);
        if (permission.subPage?.length > 0)
          final.push(...reformat(permission.subPage));
      }
    }
  });
  return final?.filter(
    (data) =>
      (data?.pageType !== null || data?.type !== "ver") && data?.isActive
  );
};

function StockLayout({
  permissions,
  setPathCheck,
  translation,
  language,
  sideNavState,
  pathCheck,
  location, // Provided by withRouter
  history // Provided by withRouter
}) {
  // Find the stock permission from all permissions
  const permission = permissions.filter(perm => perm.isActive).find(perm => perm.key === "stockApp");
  const screenSize = GetScreenSize();

  const [routerKey, setRouterKey] = useState(() => getLayoutSegment(window.location.pathname));
  const lastSyncedPathRef = useRef(null);
  const locationPathRef = useRef(location?.pathname || '');
  const routerKeyRef = useRef(routerKey);

  locationPathRef.current = location?.pathname || '';
  routerKeyRef.current = routerKey;

  useEffect(() => {
    const checkPathname = () => {
      const base = getBasename();
      const currentPath = window.location.pathname;

      // If the URL doesn't start with our basename, we're navigating away from this MFE.
      // Don't try to sync - let the host handle it.
      if (base && !currentPath.startsWith(base) && currentPath !== base) {
        return;
      }

      const strippedPathname = stripBasename(currentPath);
      const currentRouterPath = locationPathRef.current || '';

      if (pathsMatch(strippedPathname, currentRouterPath)) {
        lastSyncedPathRef.current = null;
        return;
      }

      if (history && lastSyncedPathRef.current !== strippedPathname) {
        lastSyncedPathRef.current = strippedPathname;
        history.replace(strippedPathname);
      }

      const newLayoutSegment = getLayoutSegment(currentPath);
      if (newLayoutSegment !== routerKeyRef.current) {
        setRouterKey(newLayoutSegment);
      }
    };

    return subscribeToRouteSync(checkPathname);
  }, [history]);

  const access = useMemo(() => {
    if (!permission?.subPage) return [];
    return reformat(permission.subPage)
      .sort((a, b) => {
        // Check if either 'order' is null
        if (a.order === null && b.order === null) {
          return 0; // Keep the order unchanged if both are null
        } else if (a.order === null) {
          return 1; // Place 'a' (null) after 'b' (non-null)
        } else if (b.order === null) {
          return -1; // Place 'b' (null) after 'a' (non-null)
        }
        // Sort non-null 'order' values normally
        return a.order - b.order;
      });
  }, [permission?.subPage]);
  const getLayout = (key, props, permission, reformat) => {
    switch (key) {
      // case 'settings':
      //   return (
      //     <SettingsLayout {...props} permission={permission} reformat={reformat} />
      //   )
      case 'stockSetup':
        return (
          <SetupLayout {...props} permission={permission} reformat={reformat} />
        )
      case 'stockSettings':
        return (
          <SettingsLayout {...props} permission={permission} reformat={reformat} />
        )
      case 'stockDeductionRouting':
        return (
          <DeductionRoutingLayout {...props} permission={permission} reformat={reformat} />
        )
      case 'stockPrintSettings':
        return (
          <StockPrintLayout {...props} permission={permission} reformat={reformat} />
        )
      default:
        return null;
    }
  };

  useEffect(() => {
    // Initialize pathCheck based on current location, not just permission.route
    // This ensures navigation bar shows on first render
    const currentPath = location?.pathname || window.location.pathname;

    // Find if current path matches a layout route exactly
    const exactMatch = access.find(
      (data) => data.pageType === "layout" && data.route === currentPath
    );

    if (exactMatch) {
      setPathCheck(exactMatch.route);
      return;
    }

    // Check subLayouts for nested routes. This runs before the parent-layout match below because
    // a sub-layout route is always deeper than its parent's — matching the parent first would
    // hand pathCheck back to the outer group and drop the nested tab row.
    for (const perm of access.filter((data) => data.pageType === "layout")) {
      if (perm.subLayouts?.length > 0) {
        for (const subLayout of perm.subLayouts) {
          if (currentPath === subLayout.route || currentPath.startsWith(subLayout.route + '/')) {
            setPathCheck(subLayout.route);
            return;
          }
        }
      }
    }

    // Find if current path is a child of a layout route
    const parentLayout = access.find(
      (data) => data.pageType === "layout" && currentPath.startsWith(data.route + '/')
    );

    if (parentLayout) {
      setPathCheck(parentLayout.route);
      return;
    }

    // Fallback: if we're on the base route, use it; otherwise use first available layout
    if (currentPath === permission.route || currentPath.startsWith(permission.route + '/')) {
      const firstLayout = access.find((data) => data.pageType === "layout");
      if (firstLayout) {
        setPathCheck(firstLayout.route);
      } else {
        setPathCheck(permission.route);
      }
    }
  }, [permission?.route, permission?.subPage, setPathCheck, location?.pathname, access])

  const getHeader = (key, permission, reformat) => {
    // Sub-layouts (items lists, Setup → Suppliers, Setup → SUOM, …) render two tab rows: the
    // parent's tabs on top, the sub-layout's underneath. Keyed on the shape, not the page key,
    // so a sub-layout added to the tree gets its tabs without a case here.
    if (permission?.subLayout) {
      return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '100vw'
      }}>
        <TopNavigation permission={permission} reformat={reformat} isParent />
        <TopNavigation
          permission={permission.subLayout}
          reformat={reformat}
        />
      </Box>
    }
    switch (key) {
      case "stockSetup":
        return <TopNavigation permission={permission} reformat={reformat} />;
      case "stockSettings":
        return <TopNavigation permission={permission} reformat={reformat} />;
      case "stockDeductionRouting":
        return <TopNavigation permission={permission} reformat={reformat} />;
      case "stockPrintSettings":
        return <TopNavigation permission={permission} reformat={reformat} />;
      default:
        return null;
    }
  };

  const tabsSwitch = () => {
    // Handle report route bypass
    if (pathCheck === '/report') {
      return getHeader('report', { key: 'report', route: '/report' }, reformat);
    }

    // First, try exact match
    const matchedPermission = access.find(
      (data) => data.pageType === "layout" && data.route === pathCheck
    );

    if (matchedPermission) {
      return getHeader(matchedPermission.key, matchedPermission, reformat);
    }

    // Check subLayouts for nested routes
    let partialMatchedPermission;
    access
      .filter((data) => data.pageType === "layout")
      .forEach((perm) => {
        if (perm.subLayouts?.length > 0) {
          perm.subLayouts.forEach((sub) => {
            if (sub.route === pathCheck) {
              partialMatchedPermission = { ...perm, subLayout: sub };
            }
          });
        }
      });

    if (partialMatchedPermission) {
      return getHeader(
        partialMatchedPermission.subLayout.key,
        partialMatchedPermission,
        reformat
      );
    }

    // If no exact match, try to find parent layout based on current location
    const currentPath = location?.pathname || window.location.pathname;
    const parentLayout = access.find(
      (data) => data.pageType === "layout" && currentPath.startsWith(data.route + '/')
    );

    if (parentLayout) {
      return getHeader(parentLayout.key, parentLayout, reformat);
    }

    // Check subLayouts for parent matching
    for (const perm of access.filter((data) => data.pageType === "layout")) {
      if (perm.subLayouts?.length > 0) {
        for (const subLayout of perm.subLayouts) {
          if (currentPath.startsWith(subLayout.route + '/')) {
            return getHeader(subLayout.key, { ...perm, subLayout }, reformat);
          }
        }
      }
    }

    // Return null if no match found
    return null;
  };

  // Calculate left offset based on sidebar state
  const leftOffset = sideNavState && screenSize.dynamicWidth > 700 ? '200px' : 0;
  // Top navigation tabs: kept static so the bar doesn't shift when the sidebar opens.
  // Previous sidebar-driven implementation kept inline as comments for reference.
  const Tabs = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    // position: "absolute",
    // top: 0,
    zIndex: 5,
    maxWidth: "100%",
    width: "100%",
    //marginTop: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  }));
  // const Tabs = styled("div", { shouldForwardProp: (prop) => prop !== "open" })(
  //   ({ theme, open, screensize }) => ({
  //     backgroundColor: theme.palette.background.paper,
  //     zIndex: 5,
  //     maxWidth: "100%",
  //     width: "100%",
  //     display: "flex",
  //     justifyContent: "center",
  //     alignItems: "center",
  //     transition: theme.transitions.create("padding", {
  //       easing: theme.transitions.easing.sharp,
  //       duration: theme.transitions.duration.leavingScreen,
  //     }),
  //     ...(open &&
  //       screensize.dynamicWidth > 666 && {
  //       transition: theme.transitions.create("padding", {
  //         easing: theme.transitions.easing.easeOut,
  //         duration: theme.transitions.duration.enteringScreen,
  //       }),
  //       paddingLeft: "190px",
  //     }),
  //   })
  // );

  const contentPadding = useMemo(() => {
    const pagePermission = getPagePermissionByPath(access, location?.pathname || '');
    return pagePermission?.pageType !== 'layout' ? 2 : 0;
  }, [access, location?.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* In the SMA print-settings popup each page renders its own header (with the
          ✕ close), so skip this app-level header to avoid stacking two headers. */}
      {!isEmbedded() && <Header />}
      {/* <TopNavigation permission={permission} reformat={reformat} /> */}
      {/* When opened from the SMA print-settings popup (iframe), hide the tab
          navigation so only the single opened page shows — no cross-module
          group tabs or per-template sub-tabs. */}
      {!isEmbedded() && (matchPath(location.pathname, {
        path: pathCheck + "*",
        exact: true,
      }) || pathCheck === '/report') && (
          // Previous: <Tabs open={sideNavState} screensize={screenSize}>{tabsSwitch()}</Tabs>
          <Tabs>
            {tabsSwitch()}
          </Tabs>
        )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', padding: contentPadding }}>
        <Suspense fallback={<Loading />}>
          <Switch key={routerKey}>
            <Route exact path={permission.route} render={() => <Redirect to={access[0]?.route} />} />

            {access.map((permission, i) => {
              console.log("permission", permission)
              if (permission.pageType !== "layout") {
                return (
                  <UserRoute
                    key={`${permission.uuid}`}
                    exact
                    path={permission.route}
                    component={components[permission.key]}
                    permission={permission}
                  />
                );
              }
              else
                return (
                  <Route
                    key={`${permission.uuid}`}
                    path={permission.route}
                    render={(props) =>
                      getLayout(permission.key, props, permission, reformat)
                    }
                  />
                );
            })}
          </Switch>
        </Suspense>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  permissions: state.auth.permissions,
  translation: state.translation.translation,
  language: state.translation.language,
  sideNavState: state.layout.settings?.layoutSettings?.sidebar?.open,
  pathCheck: state.layout.pathCheck
});

const mapDispatchToProps = (dispatch) => ({
  setPathCheck: (data) => dispatch(LayoutActions.setPathCheck(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(StockLayout));